import { ref, computed, watch, nextTick, Ref, ComputedRef, getCurrentInstance, unref } from 'vue'
import { get, isEqual, isEmpty } from 'lodash'
import { useSelectFormatting } from './formatting/useSelectFormatting'
import { maybeCall } from '@/utils/utils'
import { DynamicFormField } from '../DynamicFormTypes'

interface UseDynamicFieldProps {
  value: Ref<Record<string, any>>
  field: DynamicFormField
  readOnly?: boolean
  isValidating?: boolean | Ref<boolean>
  itemKey?: string
  emptyValue?: any
  emit: (event: string, ...args: any[]) => void
}

interface UseDynamicFieldReturn {
  fieldValue: Ref<any>
  validationRules: ComputedRef<Array<(v: any) => boolean | string>>
  keyValue: ComputedRef<any>
  hasError: ComputedRef<boolean>
  selectedItem: ComputedRef<any>
  label: ComputedRef<string | undefined>
  icon: ComputedRef<string>
  iconColor: ComputedRef<string>
  textColor: ComputedRef<string>
  iconTooltip: ComputedRef<string>
  inputRef: Ref<any>
  maybeCall: typeof maybeCall
  updatedFormValue: (formValue: Record<string, any>) => void
  fieldChanged: (value: any) => void
  setFieldValue: (value: any) => void
  getDynamicProperties: () => Record<string, any> | undefined
}

export function useDynamicField({
  value,
  field,
  readOnly = false,
  isValidating = false,
  itemKey = '',
  emptyValue = '',
  emit
}: UseDynamicFieldProps): UseDynamicFieldReturn {
  const instance = getCurrentInstance()
  const $t = (instance?.proxy as any)?.$t || ((key: string) => key)
  const { getSelectedItem } = useSelectFormatting()

  const fieldValue = ref<any>(emptyValue)
  const inputRef = ref<any>(null)

  // Dynamic properties can be extended by each field component
  const dynamicProperties = ref<Record<string, any>>()

  const validationRules = computed(() => {
    const rules: Array<(v: any) => boolean | string> = []

    // Only apply validation rules when isValidating is true
    // This prevents showing errors while user is filling the form
    // unref works with both Ref<boolean> and boolean
    if (!unref(isValidating)) return rules

    if (field.rules) {
      rules.push(...field.rules)
    }

    // Check if field is required (can be a function or boolean)
    const isRequired = typeof field.required === 'function' ? field.required(value.value) : field.required

    if (isRequired) {
      rules.push(v => {
        // Check for various empty values
        const isEmpty =
          v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0) || (typeof v === 'object' && Object.keys(v).length === 0)

        return !isEmpty || (field.label || 'Campo') + ' é obrigatório'
      })
    }

    return rules
  })

  const keyValue = computed(() => {
    if (itemKey) {
      return get(value.value, itemKey)
    }
    return ''
  })

  const hasError = computed(() => {
    // In Vuetify 2, hasError is a computed property on the component instance
    return inputRef.value?.hasError || false
  })

  const selectedItem = computed(() => {
    return getSelectedItem(value.value, field)
  })

  const label = computed(() => {
    const requiredSuffix = typeof field.required === 'function' ? (field.required(value.value) ? '*' : '') : field.required ? '*' : ''
    return field.label ? field.label + requiredSuffix : undefined
  })

  const icon = computed(() => {
    if (typeof field.icon === 'function') {
      return field.icon(fieldValue.value)
    } else if (field.icon !== undefined) {
      return String(field.icon)
    } else if (selectedItem.value?.icon) {
      return selectedItem.value.icon
    }
    return ''
  })

  const iconColor = computed(() => {
    if (typeof field.iconColor === 'function') {
      return field.iconColor(fieldValue.value)
    } else if (field.iconColor !== undefined) {
      return String(field.iconColor)
    } else if (selectedItem.value?.iconColor) {
      return selectedItem.value.iconColor
    }
    return ''
  })

  const textColor = computed(() => {
    if (typeof field.textColor === 'function') {
      return field.textColor(fieldValue.value)
    } else if (field.textColor !== undefined) {
      return String(field.textColor)
    } else if (selectedItem.value?.color) {
      return selectedItem.value.color
    }
    return 'default'
  })

  const iconTooltip = computed(() => {
    if (typeof field.iconTooltip === 'function') {
      return field.iconTooltip(fieldValue.value)
    } else if (field.iconTooltip !== undefined) {
      return String(field.iconTooltip)
    }
    return ''
  })

  const getDynamicProperties = () => {
    return dynamicProperties.value
  }

  const updatedFormValue = (formValue: Record<string, any>) => {
    if (field.model === undefined) return
    const newFieldValue = get(formValue, field.model)

    nextTick(() => {
      if (newFieldValue === undefined && field.default !== undefined) {
        setFieldValue(field.default)
      } else if (newFieldValue !== undefined) {
        // Convert string to number for number fields
        if (field.format === 'number' && typeof newFieldValue === 'string' && newFieldValue !== '') {
          fieldValue.value = parseFloat(newFieldValue)
        } else {
          fieldValue.value = newFieldValue
        }
      } else {
        fieldValue.value = emptyValue
      }
    })
  }

  const fieldChanged = (value: any) => {
    if (field.change && typeof field.change === 'function') {
      field.change(value)
    }
    emit('input', field, value)
    emit('change', field, value)
  }

  const setFieldValue = (value: any) => {
    fieldValue.value = value
    fieldChanged(value)
  }

  // Watch for value changes
  watch(
    value,
    (newVal, oldVal) => {
      if (field.model === undefined) return
      const newFieldValue = get(newVal, field.model)
      const oldFieldValue = get(oldVal, field.model)

      if (!isEqual(newFieldValue, oldFieldValue)) {
        updatedFormValue(newVal)
      }
    },
    { deep: true }
  )

  // Initialize value on mount
  if (!isEmpty(value.value)) {
    updatedFormValue(value.value)
  }

  return {
    fieldValue,
    validationRules,
    keyValue,
    hasError,
    selectedItem,
    label,
    icon,
    iconColor,
    textColor,
    iconTooltip,
    inputRef,
    maybeCall,
    updatedFormValue,
    fieldChanged,
    setFieldValue,
    getDynamicProperties
  }
}
